import { DOCUMENT } from '@angular/common';
import {
    DoCheck,
    EventEmitter,
    HostBinding,
    InjectionToken,
    Injector,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewContainerRef,
} from '@angular/core';
import { IChartLegendCallbackParams } from '@components/ui-chart/base-chart.types';
import {
    IChartCallbackParams,
    IChartOptions,
} from '@models';

import {
    ECharts,
    init as initChart,
} from 'echarts';
import cloneDeep from 'lodash-es/cloneDeep';
import defaultsDeep from 'lodash-es/defaultsDeep';
import {
    animationFrameScheduler,
    fromEvent,
    merge,
    of,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    filter,
    take,
    takeUntil,
} from 'rxjs/operators';

import {
    BASE_CHART_OPTIONS_MAPPER,
    COMMON_CHART_OPTIONS,
    mapBaseColorOptions,
} from './base-chart.constants';
import {
    COLOR_PROVIDER,
    ColorProvider,
    DefaultColorProvider,
} from './chart-color-provider';

export const CHART_ENABLE_ANIMATIONS = new InjectionToken<boolean>('Chart-Enable-Animations');

export abstract class BaseChart<T = IChartCallbackParams> implements OnChanges, DoCheck, OnDestroy {
    @Input()
    public legend: string[] = [];

    @Input()
    public colors: string[] = [];

    /**
     * If this is not set to true when the length of the legend will change during the lifetime of a chart, then
     * it won't be able to correctly keep count of which legend items were toggled.
     * As a side effect, the chart won't be able to detect when the legend items were changed because of something like an external
     * language change.
     * In other words, when variableLegendLength = true, the chart will no longer assume that items at the same index in the legend
     * array will always refer to the same thing.
     */
    @Input()
    public variableLegendLength = false;

    @Output()
    public segmentClick = new EventEmitter<T>();

    public chartOptions: IChartOptions;
    public legendItemsVisibility: Record<string, boolean> = {};
    public allTimeLegendItemsVisibility: Record<string, boolean> = {};

    @HostBinding('class.ui-base-chart')
    protected _hasBaseStyling = true;
    protected _chart?: ECharts;
    protected _colorProvider: ColorProvider;
    protected _legendVisibilityChange$ = new Subject<IChartLegendCallbackParams>();
    protected _destroyed$ = new Subject();
    protected abstract _viewContainerRef: ViewContainerRef;

    private _ngZone = this._injector.get<NgZone>(NgZone);
    private _chartContainer?: HTMLDivElement;
    private _isInputChanged = false;
    private _window: Window;

    constructor(protected _injector: Injector) {
        this.chartOptions = cloneDeep(COMMON_CHART_OPTIONS);
        this.chartOptions.animation = this._injector.get(CHART_ENABLE_ANIMATIONS, false);
        const document = this._injector.get(DOCUMENT);
        this._window = document.defaultView!;
        this._colorProvider = this._injector.get(COLOR_PROVIDER, new DefaultColorProvider(this._ngZone));

        this._ngZone
            .onStable
            .pipe(
                take(1),
            )
            .subscribe(() => this._create());

        this._colorProvider.changes$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._extendOptions(mapBaseColorOptions(this._colorProvider));
            });

        this._legendVisibilityChange$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe((params: IChartLegendCallbackParams) => {
                this.legendItemsVisibility = params.selected;
                Object.assign(
                    this.allTimeLegendItemsVisibility,
                    params.selected,
                );

                // Keep the state of which legend items are toggled. When the chart is updated, the ECharts' entire
                // chart options object is overwritten, so the visibility state of each legend item would be lost.
                this.chartOptions.legend.selected = this.legendItemsVisibility || {};
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        this._mapOptions(BASE_CHART_OPTIONS_MAPPER, changes);
        this._isInputChanged = true;

        if (changes.legend) {
            if (this.variableLegendLength) {
                this.legendItemsVisibility = this._mapVisibleLegendItemsByPastState();
            } else {
                this.legendItemsVisibility = this._mapVisibleLegendItemsByIndex(changes.legend);
            }

            this.chartOptions.legend.selected = this.legendItemsVisibility;
        }
    }

    ngDoCheck() {
        if (!this._isInputChanged) { return; }

        this._isInputChanged = false;
        this.update();
    }

    ngOnDestroy() {
        if (this._colorProvider instanceof DefaultColorProvider) {
            this._colorProvider.destroy();
        }

        this._destroyed$.next();
        this._destroyed$.complete();

        if (this.segmentClick instanceof EventEmitter) {
            this.segmentClick.complete();
        }

        if (this._chart) {
            this._chart.dispose();
            this._chart = undefined;
        }
    }

    public onSegmentClick(params: IChartCallbackParams) {
        this.segmentClick.emit(params as unknown as T);
    }

    public update() {
        if (!this._chart) { return; }

        this._ngZone.runOutsideAngular(() => {
            this._chart!.setOption(this.chartOptions, true);
        });
    }

    protected _extendOptions(options: IChartOptions) {
        this.chartOptions = defaultsDeep(
            cloneDeep(options),
            this.chartOptions,
        );
        this.update();
    }

    protected _onResize() { }

    protected _mapOptions(mapper: any, changes: SimpleChanges) {
        const mappedChartOptions = this._mapInputsToOptions(mapper, changes);
        this.chartOptions = defaultsDeep(mappedChartOptions, this.chartOptions);
    }

    private _mapInputsToOptions(mapper: any, changes: SimpleChanges) {
        const mappedChartOptions: any = {};

        for (const property of Object.keys(mapper)) {
            const inputKey = mapper[property];
            if (typeof inputKey === 'string') {
                if (changes[inputKey] != null) {
                    mappedChartOptions[property] = changes[inputKey].currentValue;
                }
            } else {
                mappedChartOptions[property] = this._mapInputsToOptions(inputKey, changes);
            }
        }

        return mappedChartOptions;
    }

    private _create() {
        this._chartContainer = this
            ._viewContainerRef
            .element
            .nativeElement;

        this._chart = this._ngZone.runOutsideAngular(() =>
            initChart(this._chartContainer!),
        );

        this._chart.on('click', (arg: any) =>
            this._ngZone.run(() => this.onSegmentClick(arg)),
        );

        this._chart.on('legendselectchanged', (arg: IChartLegendCallbackParams) => {
            this._ngZone.run(() => this._legendVisibilityChange$.next(arg));
        });

        this._chart.setOption(this.chartOptions);

        merge(
            of(null),
            fromEvent(this._window, 'resize')
                .pipe(
                    debounceTime(256, animationFrameScheduler),
                ),
        )
            .pipe(
                filter(_ => this._chart !== null),
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._onResize();
                this._chart!.resize();
            });
    }

    private _mapVisibleLegendItemsByPastState(): Record<string, boolean> {
        const legendItemsVisibility: Record<string, boolean> = {};

        (this.legend || []).forEach((legendName) => {
            legendItemsVisibility[legendName] =
                this.allTimeLegendItemsVisibility[legendName] != null ?
                    this.allTimeLegendItemsVisibility[legendName] :
                    true;
        });

        return legendItemsVisibility;
    }

    private _mapVisibleLegendItemsByIndex(legend: SimpleChange): Record<string, boolean> {
        const legendItemsVisibility: Record<string, boolean> = {};
        const previousVisibilityStates: boolean[] = [];

        (
            legend.previousValue ||
            legend.currentValue ||
            []
        )
            .forEach((legendName: string) => {
                previousVisibilityStates.push(
                    this.legendItemsVisibility.hasOwnProperty(legendName) ?
                        this.legendItemsVisibility[legendName] :
                        true,
                );
            });

        (this.legend || []).forEach((legendName, index) => {
            legendItemsVisibility[legendName] =
                previousVisibilityStates[index] != null ?
                    previousVisibilityStates[index] :
                    true;
        });

        return legendItemsVisibility;
    }
}
